import { type BrowserHistoryArgs } from 'instantsearch.js/es/lib/routers/history'
import { type UiState } from 'instantsearch.js/es/types/ui-state'
import { useMemo } from 'react'
import { type InstantSearchNextRouting } from 'react-instantsearch-nextjs'

import { useProductSearchContext } from '../context/productSearchContextValue'

import {
  productSearchAlgoliaIndexUiStateToRoute,
  type ProductSearchAlgoliaRouteState,
} from './productSearchAlgoliaIndexUiStateToRoute'
import { productSearchAlgoliaRouteToIndexUiState } from './productSearchAlgoliaRouteToIndexUiState'
import { productSearchOptimizeSearchQueryIfItHasPzns } from './productSearchProductIdValidationRules'

/**
 * Helper function to create a URL from the current location and route state.
 * Preserves the existing query parameters and adds the route state to it.
 */
const createUrl: BrowserHistoryArgs<ProductSearchAlgoliaRouteState>['createURL'] =
  ({ location, qsModule, routeState }) => {
    const url = new URL(location.toString())

    const qs = qsModule.parse(location.search, {
      ignoreQueryPrefix: true,
    })

    // We need to store the original query under `q` even if Algolia cleared `query` (for multiple PZN search in productSearchAlgoliaRouteToIndexUiState.ts)
    // so we can reconstruct the user input in the URL
    const prevQuery = qs.q ?? qs.query
    const finalQuery = routeState.q ?? prevQuery

    url.search = qsModule.stringify(
      {
        ...qs,
        ...routeState,
        /**
         * We need to store the original query under `q` even if Algolia cleared
         * `query` (for multiple PZN search in productSearchAlgoliaRouteToIndexUiState.ts)
         * so we can reconstruct the user input in the URL
         */
        /* eslint-disable-next-line id-length -- query param */
        q:
          typeof finalQuery === 'string'
            ? productSearchOptimizeSearchQueryIfItHasPzns(finalQuery)
                .searchQuery
            : finalQuery,
      },
      {
        addQueryPrefix: true,
      },
    )

    return url.toString()
  }

export const useProductSearchAlgoliaRouting = (): InstantSearchNextRouting<
  UiState,
  ProductSearchAlgoliaRouteState
> => {
  const {
    productSearchConfig: { baseAlgoliaConfig, sortByIndexes },
  } = useProductSearchContext()

  const { productsAlgoliaIndex } = baseAlgoliaConfig

  return useMemo(
    () => ({
      router: {
        cleanUrlOnDispose: false,
        createURL: createUrl,
      },

      stateMapping: {
        routeToState(routeState): UiState {
          return {
            [productsAlgoliaIndex]: productSearchAlgoliaRouteToIndexUiState(
              routeState,
              {
                sortByIndexes: sortByIndexes.map((item) => item.value),
              },
            ),
          }
        },
        stateToRoute(uiState): ProductSearchAlgoliaRouteState {
          return productSearchAlgoliaIndexUiStateToRoute(
            uiState[productsAlgoliaIndex],
          )
        },
      },
    }),
    [productsAlgoliaIndex, sortByIndexes],
  )
}
